import {
    FetchState,
    RiskToleranceQuestionnaireState,
    RiskToleranceQuestionnaireForm,
    RiskToleranceQuestionnaireResponse,
    RiskToleranceType,
    UserRtqEligibilityResponse,
} from "@/shared/types";
import * as drivewealthService from "@/shared/api/drivewealthService";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import { defineStore } from "pinia";

export const initialState: RiskToleranceQuestionnaireState = {
    fetchState: FetchState.Uninitialized,
    postState: FetchState.Uninitialized,
    score: 0,
    instrumentListId: 0,
    riskToleranceType: RiskToleranceType.None,
    rtqEligibilityFetchState: FetchState.Uninitialized,
    userRtqEligibilityResponse: null
};

export const useRiskToleranceQuestionnaireStore = defineStore("riskToleranceQuestionnaire", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: state => state.fetchState === FetchState.Loaded,
        isRtqEligibilityLoaded: state => state.rtqEligibilityFetchState === FetchState.Loaded,
        isPosting: state => state.fetchState === FetchState.Loading,
        riskToleranceTypeDisplayCopy: state => {
            switch (state.riskToleranceType) {
                case RiskToleranceType.Conservative:
                    return "Conservative";
                case RiskToleranceType.ModeratelyConservative:
                    return "Moderately Conservative";
                case RiskToleranceType.Moderate:
                    return "Moderate";
                case RiskToleranceType.ModeratelyAggressive:
                    return "Moderately Aggressive";
                case RiskToleranceType.Aggressive:
                    return "Aggressive";
                default:
                    return "";
            }
        }
    },
    actions: {
        setFetchState (fetchState: FetchState): void {
            this.fetchState = fetchState;
        },
        setUserRtqEligibilityFetchState (fetchState: FetchState): void {
            this.rtqEligibilityFetchState = fetchState;
        },
        setPostState (fetchState: FetchState): void {
            this.postState = fetchState;
        },
        setRiskToleranceQuestionnaireResults (response: RiskToleranceQuestionnaireResponse): void {
            this.score = response.score;
            this.riskToleranceType = response.riskToleranceType;
            this.instrumentListId = response.instrumentListId;
        },
        setUserRtqEligibilityResults (response: UserRtqEligibilityResponse): void {
            this.userRtqEligibilityResponse = response;
        },
        resetUserRtqEligibilityResults (): void {
            this.userRtqEligibilityResponse = null;
            this.rtqEligibilityFetchState = FetchState.Uninitialized;
        },
        getRiskToleranceQuestionnaireResults (): Promise<void> {
            if (this.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);

            return drivewealthService.getRiskToleranceQuestionnaireResults().then(resp => {
                this.setRiskToleranceQuestionnaireResults(resp.data);
                this.setFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getUserRtqEligibilityResponse (): Promise<void> {
            if (this.rtqEligibilityFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setUserRtqEligibilityFetchState(FetchState.Loading);
            return drivewealthService.getUserRtqEligibilityResponse().then(resp => {
                this.setUserRtqEligibilityResults(resp.data);
                this.setUserRtqEligibilityFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        storeRiskToleranceQuestionnaireResults (request: RiskToleranceQuestionnaireForm): Promise<void> {
            if (this.postState === FetchState.Loading) {
                return Promise.resolve();
            }

            this.setPostState(FetchState.Loading);

            return drivewealthService.saveRiskToleranceQuestionnaire(request).then(resp => {
                this.setRiskToleranceQuestionnaireResults(resp.data);
                this.setPostState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        clearStoredResults (): void {
            const emptyResult: RiskToleranceQuestionnaireResponse = {
                score: initialState.score,
                riskToleranceType: initialState.riskToleranceType,
                instrumentListId: 0
            };
            this.setRiskToleranceQuestionnaireResults(emptyResult);
        },
    }
});
